import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
} from '@mui/material';
import {
  AccessTime,
  Explore,
  CalendarToday,
  VolumeUp,
  Psychology,
  Groups,
  Book,
  Favorite,
  Mosque,
  Download,
  CheckCircle,
  MenuBook,
  School,
  Translate,
  Security,
  CloudSync,
  AutoAwesome,
  Gavel,
  History,
  Person,
  Star,
  Diamond,
  LibraryBooks,
  Handshake,
  Bookmark,
  LocationCity,
  PlayCircle,
  SmartToy,
  AttachMoney,
} from '@mui/icons-material';
import Navbar from './Navbar';
import Footer from './Footer';
import ScreenshotsGallery from './ScreenshotsGallery';
import PhoneMockup from './PhoneMockup';
import FeatureCard from './FeatureCard';
import StatsBar from './StatsBar';
import { BRAND, HERO_PATTERN, featureAccent } from '../theme/brand';

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Core Features
  const coreFeatures = [
    {
      icon: <AccessTime sx={{ fontSize: 40 }} />,
      title: 'Prayer Times',
      description: 'Accurate prayer time calculations based on your location with multiple calculation methods (Muslim World League, Umm al-Qura, etc.) and customizable notifications.',
      category: 'Core',
      color: '#2196F3',
    },
    {
      icon: <Explore sx={{ fontSize: 40 }} />,
      title: 'Qibla Compass',
      description: 'Beautiful Qibla compass with device sensors to find the direction of the Kaaba from anywhere in the world.',
      category: 'Core',
      color: '#4CAF50',
    },
    {
      icon: <CalendarToday sx={{ fontSize: 40 }} />,
      title: 'Islamic Calendar',
      description: 'Hijri calendar with accurate Islamic dates, holidays, and special occasions. Multiple calendar types (Umm al-Qura, Tabular, Kuwaiti) with lunar calendar support.',
      category: 'Core',
      color: '#FF9800',
    },
    {
      icon: <VolumeUp sx={{ fontSize: 40 }} />,
      title: 'Azan & Audio',
      description: 'Beautiful Azan recordings from famous mosques worldwide with high-quality Islamic audio content and background playback support.',
      category: 'Core',
      color: '#9C27B0',
    },
    {
      icon: <Psychology sx={{ fontSize: 40 }} />,
      title: 'Digital Tasbih',
      description: 'Elegant digital dhikr counter with multiple dhikr formulas, progress tracking, and beautiful Islamic-themed interface.',
      category: 'Core',
      color: '#E91E63',
    },
    {
      icon: <MenuBook sx={{ fontSize: 40 }} />,
      title: 'Quran Reader',
      description: 'Read the Holy Quran with translations, multiple recitations, and beautiful typography for an enhanced reading experience.',
      category: 'Core',
      color: '#00BCD4',
    },
  ];

  // Tijaniyya-Specific Features
  const tijaniyyaFeatures = [
    {
      icon: <Book sx={{ fontSize: 40 }} />,
      title: 'Wazifa Practice',
      description: 'Complete Wazifa with proper niyyah (intention) supplications, step-by-step guidance for daily practice, and digital counters for accurate recitation.',
      category: 'Tijaniyya',
      color: '#FF6F00',
    },
    {
      icon: <Favorite sx={{ fontSize: 40 }} />,
      title: 'Lazim Practice',
      description: 'Morning and Evening Lazim routines with proper niyyah supplications, interactive step-by-step guidance, and completion tracking.',
      category: 'Tijaniyya',
      color: '#C2185B',
    },
    {
      icon: <Mosque sx={{ fontSize: 40 }} />,
      title: 'Zikr Jum\'ah',
      description: 'Special Friday dhikr (Haylala) practices with proper niyyah supplications and complete guidance for between Asr and Maghrib.',
      category: 'Tijaniyya',
      color: '#7B1FA2',
    },
    {
      icon: <Handshake sx={{ fontSize: 40 }} />,
      title: 'Niyyah Supplications',
      description: 'Authentic intention prayers for all Tijani practices with Arabic text, transliteration, and English translation.',
      category: 'Tijaniyya',
      color: '#512DA8',
    },
    {
      icon: <Star sx={{ fontSize: 40 }} />,
      title: 'Tariqa Tijaniyyah',
      description: 'Learn about The Tijānī Path with comprehensive teachings and historical context.',
      category: 'Tijaniyya',
      color: '#0B9A6F',
    },
    {
      icon: <Gavel sx={{ fontSize: 40 }} />,
      title: 'Tijaniya Fiqh',
      description: 'The Conditions of Tijaniya Fiqh with detailed explanations and guidance.',
      category: 'Tijaniyya',
      color: '#1B5E20',
    },
    {
      icon: <LibraryBooks sx={{ fontSize: 40 }} />,
      title: 'Resources for Beginners',
      description: 'Islamic Terms & Phrases to help beginners understand Tijaniyya practices.',
      category: 'Tijaniyya',
      color: '#4CAF50',
    },
    {
      icon: <Diamond sx={{ fontSize: 40 }} />,
      title: 'Proof of Tasawwuf',
      description: 'Dhikr is the Greatest Obligation - comprehensive proof and explanations.',
      category: 'Tijaniyya',
      color: '#9C27B0',
    },
    {
      icon: <Handshake sx={{ fontSize: 40 }} />,
      title: 'Duas of Tijaniyya',
      description: 'Complete collection of Tijani Duas with proper recitation guidance.',
      category: 'Tijaniyya',
      color: '#00BCD4',
    },
    {
      icon: <Bookmark sx={{ fontSize: 40 }} />,
      title: 'Dua Khatmul Wazifa',
      description: 'Closing Prayer of Wazifa with proper instructions and meanings.',
      category: 'Tijaniyya',
      color: '#E91E63',
    },
    {
      icon: <Favorite sx={{ fontSize: 40 }} />,
      title: 'Dua Rabil Ibadi',
      description: 'Prayer to the Lord of Servants with Arabic text and translations.',
      category: 'Tijaniyya',
      color: '#673AB7',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Dua Hasbil Muhaiminu',
      description: 'The Protector is Sufficient for Me - powerful supplication with guidance.',
      category: 'Tijaniyya',
      color: '#3F51B5',
    },
  ];

  // Educational & Community Features
  const educationalFeatures = [
    {
      icon: <School sx={{ fontSize: 40 }} />,
      title: 'Islamic Lessons',
      description: 'Comprehensive Islamic education with Tijaniyya Tariqa teachings, history, scholar biographies, and interactive learning modules.',
      category: 'Education',
      color: '#607D8B',
    },
    {
      icon: <Person sx={{ fontSize: 40 }} />,
      title: 'Scholars',
      description: 'Learn from Islamic scholars and teachers with biographies, contributions, and teachings from respected Tijaniyya scholars.',
      category: 'Education',
      color: '#795548',
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: 'Community',
      description: 'Connect with fellow Muslims worldwide, share Islamic knowledge and experiences, ask questions, and get community support.',
      category: 'Community',
      color: '#E91E63',
    },
    {
      icon: <LocationCity sx={{ fontSize: 40 }} />,
      title: 'Mosque Finder',
      description: 'Find nearby mosques and prayer facilities with location-based search and directions.',
      category: 'Tools',
      color: '#795548',
    },
    {
      icon: <PlayCircle sx={{ fontSize: 40 }} />,
      title: 'Makkah Live',
      description: 'Watch live streams from the Holy Kaaba and experience the spiritual atmosphere of Makkah.',
      category: 'Tools',
      color: '#F44336',
    },
    {
      icon: <History sx={{ fontSize: 40 }} />,
      title: 'Islamic Journal',
      description: 'Reflect on your spiritual journey with a personal journal to track your progress and thoughts.',
      category: 'Tools',
      color: '#FF5722',
    },
    {
      icon: <SmartToy sx={{ fontSize: 40 }} />,
      title: 'AI Noor',
      description: 'AI-powered Islamic assistant to answer your questions and provide guidance on Islamic matters.',
      category: 'Tools',
      color: '#9C27B0',
    },
    {
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      title: 'Donation System',
      description: 'Support Islamic education and causes with transparent donation tracking, multiple payment options, and impact reports.',
      category: 'Community',
      color: '#4CAF50',
    },
    {
      icon: <Translate sx={{ fontSize: 40 }} />,
      title: 'Multi-Language Support',
      description: 'Available in English, Arabic, French, and Hausa with easy language switching and culturally appropriate translations.',
      category: 'Core',
      color: '#00ACC1',
    },
    {
      icon: <CloudSync sx={{ fontSize: 40 }} />,
      title: 'Offline Functionality',
      description: 'Core features work offline with sync across multiple devices and regular updates with new features.',
      category: 'Core',
      color: '#5C6BC0',
    },
  ];

  return (
    <Box>
      <Navbar />
      <Box sx={{ pt: 10 }}>
        {/* Hero Section */}
        <Box
          sx={{
            backgroundColor: BRAND.tealDark,
            color: 'white',
            py: { xs: 8, md: 14 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              ...HERO_PATTERN,
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-30%',
              right: '-5%',
              width: '480px',
              height: '480px',
              background: 'radial-gradient(circle, rgba(15, 163, 136, 0.2) 0%, transparent 70%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Fade in timeout={1000}>
                  <Box>
                    <Box
                      component="img"
                      src="/logo.png"
                      alt="Nur Al-Tijani"
                      sx={{
                        height: { xs: 60, sm: 80, md: 120 },
                        width: 'auto',
                        mb: { xs: 2, md: 3 },
                        filter: 'drop-shadow(0 4px 20px rgba(212, 175, 55, 0.3))',
                      }}
                    />
                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.8rem' },
                        fontWeight: 800,
                        mb: { xs: 2, md: 3 },
                        lineHeight: { xs: 1.3, md: 1.2 },
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        px: { xs: 1, md: 0 },
                      }}
                    >
                      Your Complete Islamic Companion
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: { xs: 3, md: 4 },
                        color: 'rgba(255,255,255,0.95)',
                        fontWeight: 400,
                        lineHeight: { xs: 1.6, md: 1.7 },
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        px: { xs: 1, md: 0 },
                      }}
                    >
                      Designed specifically for followers of the Tijaniyya Tariqa and all Muslims seeking spiritual growth. 
                      All-in-one Islamic tools in the palm of your hand.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, flexWrap: 'wrap', mb: { xs: 3, md: 4 }, width: '100%' }}>
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth={isMobile}
                        sx={{
                          background: `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldLight} 100%)`,
                          color: BRAND.tealDark,
                          fontWeight: 700,
                          px: { xs: 3, md: 4 },
                          py: { xs: 1.5, md: 1.8 },
                          fontSize: { xs: '0.95rem', md: '1.1rem' },
                          minHeight: { xs: '48px', md: 'auto' },
                          boxShadow: '0 8px 24px rgba(201, 162, 39, 0.35)',
                          '&:hover': {
                            background: `linear-gradient(135deg, ${BRAND.goldLight} 0%, ${BRAND.gold} 100%)`,
                            transform: { xs: 'none', md: 'translateY(-2px)' },
                            boxShadow: '0 12px 28px rgba(201, 162, 39, 0.4)',
                          },
                          '&:active': {
                            transform: 'scale(0.98)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                        startIcon={<Download />}
                        onClick={() => {
                          const element = document.querySelector('#download');
                          if (element) element.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        Download Now
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        fullWidth={isMobile}
                        sx={{
                          borderColor: 'rgba(255,255,255,0.8)',
                          borderWidth: 2,
                          color: 'white',
                          fontWeight: 600,
                          px: { xs: 3, md: 4 },
                          py: { xs: 1.5, md: 1.8 },
                          fontSize: { xs: '0.95rem', md: '1.1rem' },
                          minHeight: { xs: '48px', md: 'auto' },
                          backdropFilter: 'blur(10px)',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            transform: { xs: 'none', md: 'translateY(-2px)' },
                          },
                          '&:active': {
                            transform: 'scale(0.98)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                        onClick={() => {
                          const element = document.querySelector('#features');
                          if (element) element.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        Explore Features
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: BRAND.gold, fontSize: { xs: 20, md: 24 } }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                          Free to Download
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: BRAND.gold, fontSize: { xs: 20, md: 24 } }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                          Works Offline
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: BRAND.gold, fontSize: { xs: 20, md: 24 } }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                          Multi-Language
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Fade>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grow in timeout={1500}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <PhoneMockup />
                  </Box>
                </Grow>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <StatsBar />

        {/* Features Section */}
        <Box id="features" sx={{ py: { xs: 6, md: 12 }, backgroundColor: BRAND.surface }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: { xs: 1.5, md: 2 },
                  color: '#052F2A',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Comprehensive Features
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#666',
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: { xs: 1.6, md: 1.8 },
                  fontSize: { xs: '0.95rem', md: '1.25rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Everything you need for your spiritual journey in one beautiful, comprehensive app
              </Typography>
            </Box>

            {/* Core Features */}
            <Box sx={{ mb: { xs: 5, md: 8 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 3, md: 4 },
                  color: '#052F2A',
                  textAlign: 'center',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Core Islamic Features
              </Typography>
              <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                {coreFeatures.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <FeatureCard feature={feature} index={index} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Tijaniyya Features */}
            <Box sx={{ mb: { xs: 5, md: 8 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 3, md: 4 },
                  color: '#052F2A',
                  textAlign: 'center',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Tijaniyya-Specific Features
              </Typography>
              <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                {tijaniyyaFeatures.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <FeatureCard feature={feature} index={index} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Educational & Community Features */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 3, md: 4 },
                  color: '#052F2A',
                  textAlign: 'center',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Education & Community Features
              </Typography>
              <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                {educationalFeatures.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <FeatureCard feature={feature} index={index} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>

        {/* Screenshots Gallery Section */}
        <ScreenshotsGallery />

        {/* Why Choose Section */}
        <Box sx={{ py: { xs: 6, md: 12 }, background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: { xs: 1.5, md: 2 },
                  color: '#052F2A',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Why Choose Nur Al-Tijani?
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              {[
                {
                  icon: <CheckCircle sx={{ fontSize: 60 }} />,
                  title: 'Authentic',
                  description: 'Based on authentic Islamic teachings and Tijaniyya practices',
                  color: '#4CAF50',
                },
                {
                  icon: <AutoAwesome sx={{ fontSize: 60 }} />,
                  title: 'Comprehensive',
                  description: 'All-in-one Islamic companion with 30+ features',
                  color: '#2196F3',
                },
                {
                  icon: <Security sx={{ fontSize: 60 }} />,
                  title: 'User-Friendly',
                  description: 'Intuitive design for all ages with beautiful interface',
                  color: '#FF9800',
                },
                {
                  icon: <CloudSync sx={{ fontSize: 60 }} />,
                  title: 'Reliable',
                  description: 'Accurate calculations, offline functionality, and trusted content',
                  color: '#9C27B0',
                },
                {
                  icon: <Groups sx={{ fontSize: 60 }} />,
                  title: 'Community',
                  description: 'Connect with like-minded Muslims worldwide',
                  color: '#E91E63',
                },
                {
                  icon: <Translate sx={{ fontSize: 60 }} />,
                  title: 'Multi-Language',
                  description: 'Available in English, Arabic, French, and Hausa',
                  color: '#00BCD4',
                },
              ].map((item, index) => {
                const accent = featureAccent(index);
                return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Grow in timeout={400 + index * 100}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: { xs: 3, md: 4 },
                        borderRadius: 2,
                        background: 'white',
                        border: `1px solid ${BRAND.border}`,
                        boxShadow: 'none',
                        transition: 'border-color 0.25s ease, transform 0.25s ease',
                        '&:hover': {
                          transform: { xs: 'none', md: 'translateY(-6px)' },
                          borderColor: accent,
                          boxShadow: '0 12px 32px rgba(5, 47, 42, 0.06)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          mx: 'auto',
                          mb: { xs: 1.5, md: 2 },
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: `${accent}14`,
                          border: `1px solid ${accent}28`,
                          color: accent,
                        }}
                      >
                        {React.cloneElement(item.icon, { sx: { fontSize: { xs: 36, md: 40 } } })}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: { xs: 1.5, md: 2 }, color: BRAND.tealDark, fontSize: { xs: '1.1rem', md: '1.35rem' } }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: BRAND.textMuted, lineHeight: { xs: 1.6, md: 1.7 }, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Grow>
                </Grid>
              );})}
            </Grid>
          </Container>
        </Box>

        {/* Download Section */}
        <Box
          id="download"
          sx={{
            backgroundColor: BRAND.tealDark,
            color: 'white',
            py: { xs: 6, sm: 8, md: 12 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              ...HERO_PATTERN,
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <Box
                component="img"
                src="/logo.png"
                alt="Nur Al-Tijani"
                sx={{
                  height: { xs: 70, sm: 100, md: 150 },
                  width: 'auto',
                  mb: { xs: 3, md: 4 },
                  filter: 'drop-shadow(0 8px 32px rgba(212, 175, 55, 0.4))',
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: { xs: 2, md: 3 },
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.5rem' },
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  px: { xs: 2, md: 0 },
                  lineHeight: { xs: 1.3, md: 1.2 },
                }}
              >
                Start Your Spiritual Journey Today
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 4, md: 5 },
                  color: 'rgba(255,255,255,0.95)',
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: { xs: 1.6, md: 1.8 },
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.2rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Download Nur Al-Tijani now and experience the most comprehensive Islamic app with 30+ features designed for your spiritual growth
              </Typography>
              <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, justifyContent: 'center', flexWrap: 'wrap', width: '100%', px: { xs: 2, md: 0 } }}>
                <Button
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.nuraltijani.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  fullWidth={isMobile}
                  sx={{
                    background: `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldLight} 100%)`,
                    color: BRAND.tealDark,
                    fontWeight: 700,
                    px: { xs: 3, md: 5 },
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.95rem', md: '1.2rem' },
                    minHeight: { xs: '48px', md: 'auto' },
                    maxWidth: { xs: '100%', sm: '300px', md: 'none' },
                    boxShadow: '0 8px 24px rgba(201, 162, 39, 0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${BRAND.goldLight} 0%, ${BRAND.gold} 100%)`,
                      transform: { xs: 'none', md: 'translateY(-2px)' },
                      boxShadow: '0 12px 28px rgba(201, 162, 39, 0.4)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                  }}
                  startIcon={<Download />}
                >
                  Download on Google Play
                </Button>
                <Button
                  component="a"
                  href="https://apps.apple.com/ae/app/nur-al-tijani/id6767373503"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="large"
                  fullWidth={isMobile}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.8)',
                    borderWidth: 2,
                    color: 'white',
                    fontWeight: 600,
                    px: { xs: 3, md: 5 },
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.95rem', md: '1.2rem' },
                    minHeight: { xs: '48px', md: 'auto' },
                    maxWidth: { xs: '100%', sm: '300px', md: 'none' },
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      transform: { xs: 'none', md: 'translateY(-4px)' },
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  startIcon={<Download />}
                >
                  Download on App Store
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default LandingPage;
